import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import SkillSearch from "../Controls/SkillSearch";
import PersonSkillEditModal from "../Modals/PersonSkillEditModal";
import UserData from "../Controls/UserData";
import Helpers from "../Controls/helpers";
import API from "@src/API";
import "../../style/scss/Onboarding.scss";
import { usePersonSkillsQuery, refreshPersonSkillsData } from "../../query/usePersonSkills";
import { useCareersQuery, refreshCareers } from "../../query/useCareers";
import { useAccountProfileQuery, refreshAccountProfile } from "../../query/useAccountProfile";
import { useEmploymentQuery, refreshEmploymentData } from "../../query/useEmployment";
import { useQueryClient } from "@tanstack/react-query";

const STEP_LABELS = ["Skills", "Locations", "Matches", "Near skills", "Experience"];

const LEVEL_OPTIONS = [
  { value: 6, label: "Beginner (~6 months)" },
  { value: 18, label: "Junior (~1–2 years)" },
  { value: 36, label: "Intermediate (~3 years)" },
  { value: 72, label: "Senior (~6 years)" },
  { value: 120, label: "Guru (10+ years)" },
];

function countryList(lookups) {
  return lookups?.Countries || lookups?.countries || [];
}

function emptyLocDraft() {
  return {
    key: Helpers.uuidv4(),
    Name: "",
    CountryID: null,
    CountryName: "",
    StateID: null,
    StateName: "",
    CityID: null,
    CityName: "",
    MinAnnualCompensation: "",
    states: [],
    cities: [],
  };
}

export default function GettingStarted() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [skills, setSkills] = useState([]);
  const [careerId, setCareerId] = useState(0);
  const [openToRelocation, setOpenToRelocation] = useState(false);
  const [locations, setLocations] = useState([emptyLocDraft()]);
  const [hasExistingLocations, setHasExistingLocations] = useState(false);
  const [skillsStepComplete, setSkillsStepComplete] = useState(false);
  const [furthestStep, setFurthestStep] = useState(1);
  const [personSkillModalId, setPersonSkillModalId] = useState(null);
  const [matchCount, setMatchCount] = useState(null);
  const [gapSkills, setGapSkills] = useState([]);
  const [confirmedSkills, setConfirmedSkills] = useState([]);
  const [detailDrafts, setDetailDrafts] = useState({});
  const [lookups] = useState(() => UserData.Lookups() || {});
  const bootstrapped = useRef(false);
  const autoMatchOnLanding = useRef(false);
  const { data: skillsData, isSuccess: skillsReady } = usePersonSkillsQuery();
  const { data: careersData, isSuccess: careersReady } = useCareersQuery();
  const { data: accountData, isSuccess: accountReady } = useAccountProfileQuery();
  const { data: employmentData } = useEmploymentQuery();

  useEffect(() => {
    if (bootstrapped.current) return;
    if (!skillsReady || !careersReady || !accountReady) return;
    bootstrapped.current = true;

    const existing = (skillsData?.personSkills || []).map((ps) => ({
      ID: ps.SkillID || ps.skillID,
      Name: ps.SkillName || ps.skillName || ps.Name,
      PersonSkillID: ps.ID || ps.id,
      ExperienceMonths: ps.ExperienceMonths ?? ps.experienceMonths ?? 0,
    })).filter((s) => s.ID > 0);
    if (existing.length) setSkills(existing);

    const career = (careersData?.careers || [])[0];
    if (career) {
      setCareerId(career.ID || career.id || 0);
      setOpenToRelocation(!!(career.OpenToRelocation ?? career.openToRelocation));
    }

    const existingLocations = (accountData?.locations || []).filter((l) => (l.ID || l.id) > 0);
    const alreadyHasLocations = existingLocations.length > 0;
    setHasExistingLocations(alreadyHasLocations);

    const skillsDone = existing.length >= 3;
    setSkillsStepComplete(skillsDone);

    if (skillsDone && alreadyHasLocations) {
      autoMatchOnLanding.current = true;
      setStep(3);
      setFurthestStep(3);
    } else if (skillsDone) {
      setStep(2);
      setFurthestStep(2);
    }
  }, [skillsReady, careersReady, accountReady, skillsData, careersData, accountData]);

  // Landing with both skills and locations already filled — jump to Matches once.
  useEffect(() => {
    if (step !== 3 || !autoMatchOnLanding.current) return;
    autoMatchOnLanding.current = false;
    runMatch();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional one-shot on landing
  }, [step]);

  useEffect(() => {
    setFurthestStep((prev) => Math.max(prev, step));
  }, [step]);

  const countries = useMemo(
    () => countryList(lookups).map((c) => ({ ID: c.Key, Name: c.Value })),
    [lookups]
  );

  function skipForLater() {
    window.sessionStorage.setItem("SkipGettingStarted", "1");
    navigate("/dashboard");
  }

  async function ensureCareer(skillIds) {
    let id = careerId;
    if (!id) {
      const existing = (careersData?.careers || [])[0];
      id = existing?.ID || existing?.id || 0;
    }

    const payload = {
      ID: id || 0,
      Name: "My Career",
      Objective: "",
      OpenToPerm: true,
      OpenToContract: true,
      OpenToRelocation: openToRelocation,
      MaxTravelPercent: 0,
      InterestLevelID: 50, // LookingNow
      ResumeFormat: 1,
      Employments: [],
      Skills: skillIds,
      EmploymentTypes: [],
    };

    const res = await UserData.UpdateAsync("careers", payload, true);
    if (!res?.ok) throw new Error(res?.message || "Could not save career");
    const newId =
      res.saveResp?.jsonData?.ID ||
      res.saveResp?.jsonData?.id ||
      payload.ID;
    setCareerId(newId);
    await refreshCareers(queryClient);
    return newId;
  }

  async function saveSkill(skill) {
    const skillId = skill.ID || skill.id;
    if (!skillId) return null;
    if (skills.some((s) => (s.ID || s.id) === skillId)) return skillId;

    const payload = {
      ID: 0,
      SkillID: skillId,
      SkillName: skill.Name || skill.name,
      InterestLevelID: 1,
      ExperienceMonths: 0,
      Employments: [],
    };
    const res = await UserData.UpdateAsync("personSkills", payload, true);
    if (!res?.ok) throw new Error(res?.message || "Could not save skill");
    const personSkillId =
      res.saveResp?.jsonData?.id ||
      res.saveResp?.jsonData?.ID ||
      payload.ID;
    setSkills((prev) => [
      ...prev,
      {
        ID: skillId,
        Name: skill.Name || skill.name,
        PersonSkillID: personSkillId,
        ExperienceMonths: 0,
      },
    ]);
    return skillId;
  }

  async function onAddSkill(skill) {
    setError("");
    try {
      setBusy(true);
      await saveSkill(skill);
    } catch (e) {
      setError(e.message || "Failed to add skill");
    } finally {
      setBusy(false);
    }
  }

  async function updateSkillLevel(skill, months) {
    setError("");
    const personSkillId = skill.PersonSkillID || 0;
    if (!(personSkillId > 0)) return;

    // Post the full saved record so nothing else on the skill is lost.
    const raw = (skillsData?.personSkills || []).find((ps) => (ps.ID || ps.id) === personSkillId);
    const payload = {
      ...(raw || {
        ID: personSkillId,
        SkillID: skill.ID || skill.id,
        SkillName: skill.Name || skill.name,
        InterestLevelID: 1,
        Employments: [],
      }),
      ID: personSkillId,
      ExperienceMonths: months,
    };

    try {
      setBusy(true);
      const res = await UserData.UpdateAsync("personSkills", payload, true);
      if (!res?.ok) {
        setError(res?.message || `Could not update ${skill.Name || skill.name}.`);
        return;
      }
      await refreshPersonSkillsData(queryClient);
      setSkills((prev) =>
        prev.map((s) =>
          (s.PersonSkillID || 0) === personSkillId ? { ...s, ExperienceMonths: months } : s
        )
      );
    } catch (e) {
      setError(e.message || "Could not update skill");
    } finally {
      setBusy(false);
    }
  }

  function reloadSkillsFromQuery() {
    const existing = (skillsData?.personSkills || [])
      .map((ps) => ({
        ID: ps.SkillID || ps.skillID,
        Name: ps.SkillName || ps.skillName || ps.Name,
        PersonSkillID: ps.ID || ps.id,
        ExperienceMonths: ps.ExperienceMonths ?? ps.experienceMonths ?? 0,
      }))
      .filter((s) => s.ID > 0);
    setSkills(existing);
    if (existing.length >= 3) setSkillsStepComplete(true);
  }

  async function closePersonSkillModal() {
    setPersonSkillModalId(null);
    await refreshPersonSkillsData(queryClient);
    reloadSkillsFromQuery();
  }

  function levelOptionsFor(months) {
    const current = Number(months) || 0;
    if (current === 0) {
      return [{ value: 0, label: "Experience level…" }, ...LEVEL_OPTIONS];
    }
    if (LEVEL_OPTIONS.some((o) => o.value === current)) return LEVEL_OPTIONS;
    return [{ value: current, label: `${current} months (current)` }, ...LEVEL_OPTIONS];
  }

  async function advanceFromSkills() {
    setError("");
    if (skills.length < 3) {
      setError("Pick at least 3 skills so we can start matching openings for you.");
      return;
    }
    try {
      setBusy(true);
      const ids = skills.map((s) => s.ID || s.id);
      await ensureCareer(ids);
      setSkillsStepComplete(true);

      // Already have work locations — skip that tab and go straight to matches.
      if (hasExistingLocations) {
        setStep(3);
        await runMatch();
      } else {
        setStep(2);
      }
    } catch (e) {
      setError(e.message || "Could not continue");
    } finally {
      setBusy(false);
    }
  }

  async function onCountryChange(idx, countryId) {
    const idNum = Number(countryId) || 0;
    const country = countries.find((c) => Number(c.ID) === idNum);
    const states = idNum ? await Helpers.StatesAsync(idNum) : [];
    setLocations((prev) =>
      prev.map((loc, i) =>
        i === idx
          ? {
              ...loc,
              CountryID: idNum || null,
              CountryName: country?.Name || "",
              StateID: null,
              StateName: "",
              CityID: null,
              CityName: "",
              states: Array.isArray(states) ? states : [],
              cities: [],
            }
          : loc
      )
    );
  }

  async function onStateChange(idx, stateId) {
    const idNum = Number(stateId) || 0;
    setLocations((prev) => {
      const loc = prev[idx];
      const st = (loc.states || []).find((s) => Number(s.ID) === idNum);
      return prev.map((l, i) =>
        i === idx
          ? {
              ...l,
              StateID: idNum || null,
              StateName: st?.Name || "",
              CityID: null,
              CityName: "",
            }
          : l
      );
    });
    const cities = idNum ? await Helpers.CitiesAsync(idNum) : [];
    setLocations((prev) =>
      prev.map((loc, i) =>
        i === idx ? { ...loc, cities: Array.isArray(cities) ? cities : [] } : loc
      )
    );
  }

  function patchLoc(idx, patch) {
    setLocations((prev) => prev.map((loc, i) => (i === idx ? { ...loc, ...patch } : loc)));
  }

  async function advanceFromLocations() {
    setError("");
    const valid = locations.filter((l) => l.CountryID);
    if (!valid.length) {
      setError("Add at least one place you’re willing to work.");
      return;
    }
    for (const l of valid) {
      const floor = Number(l.MinAnnualCompensation);
      if (!(floor > 0)) {
        setError("Each location needs the annual income you need there.");
        return;
      }
    }

    try {
      setBusy(true);
      const skillIds = skills.map((s) => s.ID || s.id);
      await ensureCareer(skillIds);

      for (const l of valid) {
        const payload = {
          ID: 0,
          Name: l.Name || [l.CityName, l.StateName, l.CountryName].filter(Boolean).join(", "),
          CountryID: l.CountryID,
          CountryName: l.CountryName,
          StateID: l.StateID,
          StateName: l.StateName,
          CityID: l.CityID,
          CityName: l.CityName,
          MinAnnualCompensation: Number(l.MinAnnualCompensation),
        };
        const res = await UserData.UpdateAsync("locations", payload, true);
        if (!res?.ok) throw new Error(res?.message || "Could not save a location");
      }

      await refreshAccountProfile(queryClient);
      setHasExistingLocations(true);
      setStep(3);
      await runMatch();
    } catch (e) {
      setError(e.message || "Could not save locations");
    } finally {
      setBusy(false);
    }
  }

  async function runMatch() {
    setError("");
    setBusy(true);
    try {
      const matchResp = await API.postAsync("/FindJobsForPerson", {});
      if (!matchResp?.ok) {
        // Queue may still process; fall through to gap skills / coaching counts.
        console.warn("FindJobsForPerson failed", matchResp?.message);
      }
      const gapResp = await API.getAsync("/Onboarding/GapSkills");
      if (gapResp?.ok && gapResp.jsonData) {
        const data = gapResp.jsonData;
        setMatchCount(data.ActiveMatchCount ?? data.activeMatchCount ?? 0);
        setGapSkills(data.Skills || data.skills || []);
      } else {
        const coach = await API.getAsync("/SeekerCoaching");
        setMatchCount(coach?.jsonData?.ActiveMatchCount ?? coach?.jsonData?.activeMatchCount ?? 0);
        setGapSkills([]);
      }
    } catch (e) {
      setError(e.message || "Matching ran into a problem — you can still continue.");
    } finally {
      setBusy(false);
    }
  }

  function confirmGapSkill(skill) {
    const id = skill.SkillID || skill.skillID;
    if (confirmedSkills.some((s) => (s.SkillID || s.skillID) === id)) return;
    setConfirmedSkills((prev) => [...prev, skill]);
    setDetailDrafts((prev) => ({
      ...prev,
      [id]: {
        ExperienceMonths: 36,
        EmployerName: "",
        Title: "",
        StartDate: "",
        employmentId: 0,
        useExisting: false,
      },
    }));
    setGapSkills((prev) => prev.filter((s) => (s.SkillID || s.skillID) !== id));
  }

  function skipGapSkill(skill) {
    const id = skill.SkillID || skill.skillID;
    setGapSkills((prev) => prev.filter((s) => (s.SkillID || s.skillID) !== id));
  }

  function patchDetail(skillId, patch) {
    setDetailDrafts((prev) => ({
      ...prev,
      [skillId]: { ...prev[skillId], ...patch },
    }));
  }

  async function saveConfirmedSkills() {
    setError("");
    if (!confirmedSkills.length) {
      setStep(3);
      await runMatch();
      return;
    }

    const employments = employmentData?.employmentHistory || [];

    try {
      setBusy(true);
      for (const skill of confirmedSkills) {
        const skillId = skill.SkillID || skill.skillID;
        const draft = detailDrafts[skillId] || {};
        let employmentId = Number(draft.employmentId) || 0;

        if (!employmentId) {
          if (!draft.Title?.trim() || !draft.EmployerName?.trim() || !draft.StartDate) {
            setError(`Add where you used ${skill.Name || skill.name} (employer, title, start date).`);
            setBusy(false);
            return;
          }

          // Resolve or create employer by search, then quick-add employment.
          let employerId = 0;
          const found = await Helpers.EmployersAsync(draft.EmployerName.trim());
          const match = (found || []).find(
            (e) => (e.Name || e.name || "").toLowerCase() === draft.EmployerName.trim().toLowerCase()
          );
          if (match) {
            employerId = match.ID || match.id;
          } else if (found?.length) {
            employerId = found[0].ID || found[0].id;
          } else {
            const createEmp = await API.postAsync("/Employer", {
              ID: 0,
              Name: draft.EmployerName.trim(),
            });
            employerId =
              createEmp?.jsonData?.ID ||
              createEmp?.jsonData?.id ||
              (typeof createEmp?.jsonData === "number" ? createEmp.jsonData : 0);
          }
          if (!employerId) {
            setError(`Could not save employer for ${skill.Name || skill.name}.`);
            setBusy(false);
            return;
          }

          const empPayload = {
            ID: 0,
            Title: draft.Title.trim(),
            EmployerID: employerId,
            StartDate: draft.StartDate,
            EndDate: null,
            EmploymentTypeID: 10,
            PayRate: 0,
            ShortDescription: "",
            Responsibilities: "",
          };
          const empRes = await UserData.UpdateAsync("employmentHistory", empPayload, true);
          if (!empRes?.ok) {
            setError(empRes?.message || "Could not save employment");
            setBusy(false);
            return;
          }
          employmentId =
            empRes.saveResp?.jsonData?.ID ||
            empRes.saveResp?.jsonData?.id ||
            empPayload.ID;
          await refreshEmploymentData(queryClient);
        }

        if (!employmentId && employments.length === 1) {
          employmentId = employments[0].ID || employments[0].id;
        }

        if (!employmentId) {
          setError(`Link an employment for ${skill.Name || skill.name} before saving.`);
          setBusy(false);
          return;
        }

        const payload = {
          ID: 0,
          SkillID: skillId,
          SkillName: skill.Name || skill.name,
          InterestLevelID: 1,
          ExperienceMonths: Number(draft.ExperienceMonths) || 36,
          Employments: [employmentId],
        };
        const res = await UserData.UpdateAsync("personSkills", payload, true);
        if (!res?.ok) throw new Error(res?.message || "Could not save skill");
      }

      setConfirmedSkills([]);
      setDetailDrafts({});
      await refreshPersonSkillsData(queryClient);
      await refreshEmploymentData(queryClient);
      setStep(3);
      await runMatch();
      toast.success("Nice — those skills help us find better openings for you.");
    } catch (e) {
      setError(e.message || "Could not save skill details");
    } finally {
      setBusy(false);
    }
  }

  function finish() {
    window.sessionStorage.setItem("SkipGettingStarted", "1");
    navigate("/dashboard");
  }

  const existingEmployments = employmentData?.employmentHistory || [];

  function goToStep(n) {
    if (n < 1 || n > STEP_LABELS.length || n === step) return;
    if (!canVisitStep(n)) return;
    setError("");
    setStep(n);
    if (n === 3 && matchCount == null) {
      runMatch();
    }
  }

  function canVisitStep(n) {
    if (n === step) return true;
    if (n === 1) return true;

    const skillsOk = skillsStepComplete || skills.length >= 3;
    if (n === 2) return skillsOk || hasExistingLocations || furthestStep >= 2;

    // Matches unlock after locations are saved, or when locations were already on the profile.
    if (n === 3) {
      return skillsOk && (hasExistingLocations || furthestStep >= 3);
    }

    if (n === 4) {
      return furthestStep >= 4 || (furthestStep >= 3 && (gapSkills.length > 0 || confirmedSkills.length > 0));
    }

    if (n === 5) {
      return furthestStep >= 5 || confirmedSkills.length > 0;
    }

    return false;
  }

  function stepClass(n) {
    const base =
      n === step
        ? "is-active"
        : n === 1 && skillsStepComplete
          ? "is-done"
          : n === 2 && (hasExistingLocations || furthestStep > 2)
            ? "is-done"
            : n < step || (n < furthestStep && n !== step)
              ? "is-done"
              : undefined;
    const visitable = canVisitStep(n);
    return [base, visitable ? "is-clickable" : "is-locked"].filter(Boolean).join(" ");
  }

  function backFromMatches() {
    goToStep(canVisitStep(2) ? 2 : 1);
  }

  return (
    <div className="pagebody getting-started">
      <div className="getting-started__header">
        <h1>Let&apos;s find openings that fit you</h1>
        <p>
          A few skills and where you want to work — then CareerMaster matches openings for you.
          This is coaching, not a job board.
        </p>
      </div>

      <ol className="getting-started__steps" aria-label="Progress">
        {STEP_LABELS.map((label, i) => {
          const n = i + 1;
          const visitable = canVisitStep(n);
          return (
            <li key={label} className={stepClass(n)}>
              <button
                type="button"
                disabled={!visitable || busy}
                aria-current={n === step ? "step" : undefined}
                onClick={() => goToStep(n)}
              >
                {n}. {label}
              </button>
            </li>
          );
        })}
      </ol>

      {error ? <p className="getting-started__error">{error}</p> : null}

      {step === 1 ? (
        <div className="getting-started__panel">
          <h2>What are you great at?</h2>
          <p className="byline">Pick at least 3 skills. You can deepen the list after we show matches.</p>
          <SkillSearch label="Search for a skill" onSelect={onAddSkill} allowCreate />
          {skills.length > 0 ? (
            <ul className="getting-started__skill-list" aria-label="Your skills">
              {skills.map((s) => {
                const personSkillId = s.PersonSkillID || 0;
                return (
                  <li key={s.ID || s.id}>
                    {personSkillId > 0 ? (
                      <button
                        type="button"
                        className="getting-started__skill-name getting-started__skill-name--link"
                        disabled={busy}
                        onClick={() => setPersonSkillModalId(personSkillId)}
                      >
                        {s.Name || s.name}
                      </button>
                    ) : (
                      <span className="getting-started__skill-name">{s.Name || s.name}</span>
                    )}
                    <select
                      aria-label={`Experience level for ${s.Name || s.name}`}
                      value={Number(s.ExperienceMonths) || 0}
                      disabled={busy || !(personSkillId > 0)}
                      onChange={(e) => updateSkillLevel(s, Number(e.target.value))}
                    >
                      {levelOptionsFor(s.ExperienceMonths).map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </select>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="byline">No skills yet — search above to add your first one.</p>
          )}
          {personSkillModalId != null ? (
            <PersonSkillEditModal
              personSkillId={personSkillModalId}
              onClose={closePersonSkillModal}
            />
          ) : null}
          <div className="getting-started__actions">
            <button type="button" className="getting-started__skip" onClick={skipForLater}>
              I&apos;ll do this later
            </button>
            <span className="spacer" />
            <button type="button" disabled={busy || skills.length < 3} onClick={advanceFromSkills}>
              Continue
            </button>
          </div>
        </div>
      ) : null}

      {step === 2 ? (
        <div className="getting-started__panel">
          <h2>Where do you want to work?</h2>
          <p className="byline">
            Add each place with the annual income you need there — pay floors are cost-of-living specific.
          </p>

          {locations.map((loc, idx) => (
            <div key={loc.key} className="getting-started__loc-card">
              <h3>Location {idx + 1}</h3>
              <div className="getting-started__field">
                <label htmlFor={`gs-country-${idx}`}>Country</label>
                <select
                  id={`gs-country-${idx}`}
                  value={loc.CountryID || ""}
                  onChange={(e) => onCountryChange(idx, e.target.value)}
                >
                  <option value="">Select…</option>
                  {countries.map((c) => (
                    <option key={c.ID} value={c.ID}>
                      {c.Name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="getting-started__field">
                <label htmlFor={`gs-state-${idx}`}>State / province</label>
                <select
                  id={`gs-state-${idx}`}
                  value={loc.StateID || ""}
                  disabled={!loc.CountryID}
                  onChange={(e) => onStateChange(idx, e.target.value)}
                >
                  <option value="">Select…</option>
                  {(loc.states || []).map((s) => (
                    <option key={s.ID} value={s.ID}>
                      {s.Name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="getting-started__field">
                <label htmlFor={`gs-city-${idx}`}>City</label>
                <select
                  id={`gs-city-${idx}`}
                  value={loc.CityID || ""}
                  disabled={!loc.StateID}
                  onChange={(e) => {
                    const id = Number(e.target.value) || 0;
                    const city = (loc.cities || []).find((c) => Number(c.ID) === id);
                    patchLoc(idx, { CityID: id || null, CityName: city?.Name || "" });
                  }}
                >
                  <option value="">Any / select…</option>
                  {(loc.cities || []).map((c) => (
                    <option key={c.ID} value={c.ID}>
                      {c.Name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="getting-started__field">
                <label htmlFor={`gs-pay-${idx}`}>Annual income you need here</label>
                <input
                  id={`gs-pay-${idx}`}
                  type="number"
                  min="1"
                  step="1000"
                  value={loc.MinAnnualCompensation}
                  onChange={(e) => patchLoc(idx, { MinAnnualCompensation: e.target.value })}
                  placeholder="e.g. 120000"
                />
              </div>
            </div>
          ))}

          <div className="getting-started__actions">
            <button type="button" onClick={() => setLocations((prev) => [...prev, emptyLocDraft()])}>
              Add another location
            </button>
          </div>

          <label className="getting-started__field" style={{ flexDirection: "row", alignItems: "center", gap: "0.5rem" }}>
            <input
              type="checkbox"
              checked={openToRelocation}
              onChange={(e) => setOpenToRelocation(e.target.checked)}
            />
            I&apos;m open to relocating
          </label>

          <div className="getting-started__actions">
            <button type="button" onClick={() => setStep(1)} disabled={busy}>
              Back
            </button>
            <span className="spacer" />
            <button type="button" disabled={busy} onClick={advanceFromLocations}>
              Find matches
            </button>
          </div>
        </div>
      ) : null}

      {step === 3 ? (
        <div className="getting-started__panel">
          <h2>Here&apos;s where you stand</h2>
          <p className="byline">CareerMaster finds openings for you based on your skills and preferences.</p>
          {busy && matchCount == null ? (
            <p>Matching openings…</p>
          ) : (
            <>
              <div className="getting-started__match-count" aria-live="polite">
                {matchCount ?? "—"}
              </div>
              <p className="byline">
                {(matchCount ?? 0) >= 5
                  ? "Solid start. Adding detail helps us narrow to the best fits."
                  : (matchCount ?? 0) > 0
                    ? "A few openings already fit. Confirming nearby skills can unlock more."
                    : "Few or no matches yet — widening locations, adjusting income floors, or confirming near skills usually helps."}
              </p>
            </>
          )}
          <div className="getting-started__actions">
            <button type="button" onClick={backFromMatches} disabled={busy}>
              Back
            </button>
            <button type="button" onClick={() => runMatch()} disabled={busy}>
              Refresh matches
            </button>
            <span className="spacer" />
            {(gapSkills.length > 0 || confirmedSkills.length > 0) ? (
              <button type="button" disabled={busy} onClick={() => setStep(4)}>
                Improve matches
              </button>
            ) : null}
            <button type="button" disabled={busy} onClick={finish}>
              Go to Dashboard
            </button>
          </div>
        </div>
      ) : null}

      {step === 4 ? (
        <div className="getting-started__panel">
          <h2>Skills that unlock nearby openings</h2>
          <p className="byline">
            You&apos;re close to roles that also ask for these. Confirming them helps us find better fits.
          </p>
          {gapSkills.length === 0 ? (
            <p>No more coaching suggestions right now — great progress.</p>
          ) : (
            <ul className="getting-started__gap-list">
              {gapSkills.map((gs) => {
                const id = gs.SkillID || gs.skillID;
                return (
                  <li key={id}>
                    <div>
                      <strong>{gs.Name || gs.name}</strong>
                      <div className="byline">{gs.SuggestedPrompt || gs.suggestedPrompt}</div>
                    </div>
                    <div className="getting-started__actions" style={{ marginTop: 0 }}>
                      <button type="button" onClick={() => confirmGapSkill(gs)}>
                        I have experience
                      </button>
                      <button type="button" className="getting-started__skip" onClick={() => skipGapSkill(gs)}>
                        Skip
                      </button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
          <div className="getting-started__actions">
            <button type="button" onClick={() => setStep(3)}>
              Back
            </button>
            <span className="spacer" />
            <button
              type="button"
              disabled={busy || confirmedSkills.length === 0}
              onClick={() => setStep(5)}
            >
              Next: experience details
            </button>
            <button type="button" onClick={finish}>
              Finish
            </button>
          </div>
        </div>
      ) : null}

      {step === 5 ? (
        <div className="getting-started__panel">
          <h2>Where did you use these skills?</h2>
          <p className="byline">
            Experience level plus at least one employment helps us score openings accurately.
          </p>
          {confirmedSkills.map((skill) => {
            const skillId = skill.SkillID || skill.skillID;
            const draft = detailDrafts[skillId] || {};
            return (
              <div key={skillId} className="getting-started__loc-card">
                <h3>{skill.Name || skill.name}</h3>
                <div className="getting-started__field">
                  <label>Experience level</label>
                  <select
                    value={draft.ExperienceMonths || 36}
                    onChange={(e) => patchDetail(skillId, { ExperienceMonths: Number(e.target.value) })}
                  >
                    {LEVEL_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
                {existingEmployments.length > 0 ? (
                  <div className="getting-started__field">
                    <label>Link an existing employment</label>
                    <select
                      value={draft.employmentId || ""}
                      onChange={(e) =>
                        patchDetail(skillId, { employmentId: Number(e.target.value) || 0 })
                      }
                    >
                      <option value="">Quick-add instead…</option>
                      {existingEmployments.map((eh) => (
                        <option key={eh.ID || eh.id} value={eh.ID || eh.id}>
                          {eh.Title || eh.title} ({eh.EmployerName || eh.employerName || "Employer"})
                        </option>
                      ))}
                    </select>
                  </div>
                ) : null}
                {!draft.employmentId ? (
                  <>
                    <div className="getting-started__field">
                      <label>Employer</label>
                      <input
                        value={draft.EmployerName || ""}
                        onChange={(e) => patchDetail(skillId, { EmployerName: e.target.value })}
                      />
                    </div>
                    <div className="getting-started__field">
                      <label>Title</label>
                      <input
                        value={draft.Title || ""}
                        onChange={(e) => patchDetail(skillId, { Title: e.target.value })}
                      />
                    </div>
                    <div className="getting-started__field">
                      <label>Start date</label>
                      <input
                        type="date"
                        value={draft.StartDate || ""}
                        onChange={(e) => patchDetail(skillId, { StartDate: e.target.value })}
                      />
                    </div>
                  </>
                ) : null}
              </div>
            );
          })}
          <div className="getting-started__actions">
            <button type="button" onClick={() => setStep(4)} disabled={busy}>
              Back
            </button>
            <span className="spacer" />
            <button type="button" disabled={busy} onClick={saveConfirmedSkills}>
              Save &amp; see updated matches
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
